import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { spacingVars } from '@astryxdesign/core/theme/tokens.stylex';
import { Selector } from '@astryxdesign/core/Selector';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Icon } from '@astryxdesign/core/Icon';
import type { FilterCondition, FilterOperator } from '@qronstat/shared';
import { useApp } from '../../app/context';
import { text } from '../../model/meta';
import { normalizeValues, operatorSpec, operatorsFor } from '@qronstat/ui/filters';
import { defaultOperator, filterableDimensions } from './model';
import { ValueInput } from './ValueInput';
function operatorShapeKeeps(from: FilterOperator, to: FilterOperator): boolean {
  const a = operatorSpec(from);
  const b = operatorSpec(to);
  return a.shape === b.shape || a.shape === 'list' && b.shape === 'single' || a.shape === 'single' && b.shape === 'list';
}
const noScope: readonly string[] = [];
export function ConditionRow({
  condition,
  onChange,
  onRemove,
  narrow,
  scope = noScope
}: {
  condition: FilterCondition;
  onChange: (c: FilterCondition) => void;
  onRemove: () => void;
  narrow?: boolean;
  scope?: readonly string[];
}) {
  const intl = useIntl();
  const {
    meta,
    locale
  } = useApp();
  const dims = useMemo(() => {
    const list = filterableDimensions(meta, scope);
    const current = meta?.dimension(condition.member);
    return current && !list.includes(current) ? [...list, current] : list;
  }, [meta, scope, condition.member]);
  const options = useMemo(() => {
    const byCategory = new Map<string, {
      value: string;
      label: string;
    }[]>();
    for (const d of dims) {
      const list = byCategory.get(d.category) ?? [];
      list.push({
        value: d.id,
        label: text(d.title, locale)
      });
      byCategory.set(d.category, list);
    }
    return [...byCategory.entries()].map(([category, opts]) => ({
      type: 'section' as const,
      title: intl.formatMessage({
        id: `category.${category}`,
        defaultMessage: category
      }),
      options: opts.sort((a, b) => a.label.localeCompare(b.label, locale))
    }));
  }, [dims, locale, intl]);
  const dim = meta?.dimension(condition.member);
  const operators = dim ? operatorsFor(dim.type) : [];
  return <div {...{
    0: {
      className: "xrvj5dj x1mfsf01 x1txdalj x6s0dn4"
    },
    1: {
      className: "xrvj5dj x1txdalj x6s0dn4 xju1xpo x1ngg2t4"
    }
  }[!!narrow << 0]}>
      <Selector label={intl.formatMessage({
      id: 'filters.field'
    })} isLabelHidden size="sm" hasSearch value={condition.member} onChange={v => {
      if (!v) return;
      const next = meta?.dimension(v);
      onChange({
        member: v,
        operator: defaultOperator(next)
      });
    }} options={options} searchPlaceholder={intl.formatMessage({
      id: 'common.searchPlaceholder'
    })} />
      {narrow ? <IconButton label={intl.formatMessage({
      id: 'filters.removeCondition'
    })} icon={<Icon icon="close" />} variant="ghost" size="sm" onClick={onRemove} /> : null}
      <div {...{
      0: {},
      1: {
        className: "x1osaytk"
      }
    }[!!narrow << 0]}>
        <Selector label={intl.formatMessage({
        id: 'filters.operator'
      })} isLabelHidden size="sm" value={condition.operator} onChange={v => {
        if (!v) return;
        const operator = v as FilterOperator;
        const values = normalizeValues(operator, condition.values);
        onChange({
          member: condition.member,
          operator,
          ...(values && operatorShapeKeeps(condition.operator, operator) ? {
            values
          } : {})
        });
      }} options={operators.map(o => ({
        value: o,
        label: intl.formatMessage({
          id: `filters.op.${o}`
        })
      }))} />
      </div>
      <div {...{
      0: {},
      1: {
        className: "x1osaytk"
      }
    }[!!narrow << 0]}>
        {dim ? <ValueInput dim={dim} condition={condition} onChange={values => onChange({
        ...condition,
        values
      })} /> : null}
      </div>
      {narrow ? null : <IconButton label={intl.formatMessage({
      id: 'filters.removeCondition'
    })} icon={<Icon icon="close" />} variant="ghost" size="sm" onClick={onRemove} />}
    </div>;
}